import React, { useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import MobileMenu, { MenuPeekBar } from './MobileMenu';
import ClientMenuSheet from '../ClientMenuSheet';
import NotificationsBell from '../NotificationsBell';
import useAssistantAvailable from '../../assistant/useAssistantAvailable';
import usePriceBoardEnabled from '../../prices/usePriceBoardEnabled';
import { toast } from '../ui/toast';

const Header = () => {
  const { user, setUser } = useContext(AuthContext);
  const assistantOn = useAssistantAvailable(user);
  const pricesOn = usePriceBoardEnabled(user) === true;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // The public landing at "/" renders its own top bar (Landing.js).
  // Suppress the app Header there so unauthenticated visitors aren't
  // shown two stacked navs. Must sit AFTER all hooks — early-returning
  // before useRef would change the hook count when the route changes
  // and trigger React error #310.
  const suppressOnLanding = (!user && location.pathname === '/')
    // The redesign's review mount owns its own chrome for every session.
    || location.pathname === '/test-landing';
  // Receipt routes render a standalone document — no app chrome. Lets
  // anonymous clients open a receipt link from email without a logged-
  // in app shell wrapping it (and keeps print output clean).
  const suppressOnReceipt = location.pathname.startsWith('/receipt/');
  // Released-records documents are standalone printables too.
  const suppressOnRecords = location.pathname.startsWith('/records/');
  // A payment link is the provider's bill to their client, like a receipt.
  const suppressOnPay = location.pathname.startsWith('/pay/');
  // Public provider profile (/p/:joinCode) is the provider's own
  // shopfront. Their logo + brand should own the page; the Avayble
  // Header would compete with that. The page has its own "powered
  // by Avayble" footer for brand presence.
  const suppressOnPublicProfile = location.pathname.startsWith('/p/');
  // On lg+ provider routes, ProviderShell renders its own desktop
  // chrome (sidebar + topbar) — having both stacked would be a
  // disaster. Don't fully suppress the Header at all sizes (mobile
  // still needs it); instead we add the lg:hidden class on the
  // returned <nav> below so the legacy chrome disappears only on
  // large screens.
  const isProviderRoute = location.pathname.startsWith('/provider');
  // Client routes that wrap with <ClientShell> (which renders its own
  // lg+ sidebar). Header.js must hide at lg+ on these too, otherwise
  // it stacks above the shell. Keep this list in lockstep with the
  // <ClientShell> wraps in App.js.
  const CLIENT_SHELL_PATHS = new Set([
    '/', // client dashboard
    '/my-profile', '/my-intake', '/book', '/availability',
    '/my-bookings', '/packages', '/my-packages', '/my-membership',
  ]);
  const isClientShellRoute = CLIENT_SHELL_PATHS.has(location.pathname);
  // /notifications renders inside ProviderShell for providers (the
  // legacy top-nav design is retired on provider desktop) — hide the
  // Header at lg+ there too. Clients keep the Header: their inbox is
  // not shell-wrapped.
  const isProviderNotifications = location.pathname === '/notifications'
    && user?.accountType === 'PROVIDER';

  const handleLogout = async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setUser(null);
        window.location.replace('/login');
        return;
      }
      toast.error("Couldn't sign you out — try again in a moment.");
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error("Couldn't sign you out — check your connection and try again.");
    }
  };

  const getNavLinks = () => {
    if (!user) {
      return [
        { href: '/login', label: 'Login' },
        { href: '/signup', label: 'Sign Up' },
        { href: '/privacy-policy.html', label: 'Privacy Policy' },
        { href: '/terms-of-service.html', label: 'Terms of Service' }
      ];
    }

    if (user.accountType === 'PROVIDER') {
      return [
        { href: '/provider', label: 'Dashboard' },
        { href: '/provider/appointments', label: 'Appointments' },
        { href: '/provider/events', label: 'Events' },
        { href: '/provider/availability', label: 'Availability' },
        { href: '/provider/schedule-template', label: 'Weekly Hours' },
        { href: '/provider/locations', label: 'Locations' },
        pricesOn ? { href: '/provider/prices', label: 'Prices' } : { href: '/provider/services', label: 'Services' },
        { href: '/provider/packages', label: 'Packages' },
        { href: '/provider/roster', label: 'Roster' },
        { href: '/provider/notes', label: 'SOAP notes' },
        // Shown only when the assistant is on for this practice.
        ...(assistantOn ? [{ href: '/provider/assistant', label: 'Assistant' }] : []),
        { href: '/provider/outreach', label: 'Outreach' },
        { href: '/provider/reports', label: 'Reports' },
        { href: '/provider/settings', label: 'Settings' }
      ];
    }

    return [
      { href: '/', label: 'Dashboard' },
      { href: '/book', label: 'Book Appointment' },
      { href: '/availability', label: "Provider's Calendar" },
      { href: '/my-bookings', label: 'My Bookings' },
      { href: '/my-packages', label: 'My Packages' },
      { href: '/my-membership', label: 'Membership' },
      { href: '/packages', label: 'Buy Packages' },
      { href: '/privacy-policy.html', label: 'Privacy Policy' }
    ];
  };

  const navLinks = getNavLinks();
  const isActive = (path) => location.pathname === path;

  if (suppressOnLanding || suppressOnReceipt || suppressOnRecords || suppressOnPay || suppressOnPublicProfile) {
    return null;
  }

  return (
    <>
    <nav
      className={`fixed top-0 left-0 right-0 bg-paper-elev shadow-atelier-sm border-b border-line z-50 ${(isProviderRoute || isClientShellRoute || isProviderNotifications) ? 'lg:hidden' : ''}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="block">
              <img
                src="/imgs/avayble_logo.png"
                alt="Avayble"
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Mobile-only bell — desktop has its own placement in the
              sidebar shell. Hidden on sm+ where the desktop nav block
              below renders the bell instead. */}
          {user && (
            <div className="flex items-center sm:hidden">
              <NotificationsBell />
            </div>
          )}

          {/* Desktop Navigation */}
          <div className="hidden sm:flex sm:space-x-6 sm:items-center">
            {navLinks.map((link) => {
              const isExternal = link.href.startsWith('/privacy-policy');
              const classes = `${
                isActive(link.href)
                  ? 'border-accent text-accent'
                  : 'border-transparent text-ink-3 hover:text-ink-2 hover:border-line'
              } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors`;

              return isExternal ? (
                <a key={link.href} href={link.href} className={classes}>
                  {link.label}
                </a>
              ) : (
                <Link key={link.href} to={link.href} className={classes}>
                  {link.label}
                </Link>
              );
            })}
            {user && (
              <>
                <NotificationsBell />
                {user.accountType === 'CLIENT' && (
                  <Link
                    to="/my-profile"
                    className={`${
                      isActive('/my-profile')
                        ? 'border-accent text-accent'
                        : 'border-transparent text-ink-3 hover:text-ink-2 hover:border-line'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                  >
                    Profile
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="ml-2 inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-accent hover:bg-accent-ink transition-colors shadow-sm"
                >
                  Logout
                </button>
              </>
            )}
          </div>

        </div>
      </div>

      {/* Mobile menu trigger — copper peek bar from MobileMenu.js.
          The bar shares its responsive class with the sheet so they
          appear/disappear together. SIGNED-IN ONLY: with no user there
          is no account to navigate — signed-out visitors on /login and
          the other auth routes were getting a provider-labeled menu
          built from fallback strings, every link a bounce back to
          login (Ivan's catch, 2026-08-27). */}
      {/* NOTE: the peek bar and menu sheet render OUTSIDE this <nav>
          (below) — the nav is fixed z-50, which makes it a stacking
          context; anything inside it out-stacks EVERY page element
          below z-50 regardless of its own z class. The peek bar's
          z-30 was silently promoted to 50 and covered page-level
          bottom bars (the intake form's z-40 submit bar, 2026-08-27:
          "the intake form has no submit button"). Outside the nav its
          z-30 is real, and page bars at z-40 win — the intended order. */}
    </nav>

      {user && <MenuPeekBar onClick={() => setMobileMenuOpen(true)} />}

      {/* Mobile menu — bottom sheet, rendered as a sibling of the nav
          so it escapes both its max-w container and its stacking
          context. CLIENTS get the ONE unified menu (ClientMenuSheet —
          the same sheet the provider page's account pill opens, so
          the two doors can never drift); providers keep MobileMenu. */}
      {user && user.accountType === 'CLIENT' ? (
        <ClientMenuSheet
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />
      ) : user ? (
        <MobileMenu
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          navLinks={navLinks}
          user={user}
          onLogout={handleLogout}
        />
      ) : null}
    </>
  );
};

export default Header;
